// Phase-16 R3 — Avatar primitive.  (R-PROFILE-GENDER: unified single avatar.)
//
// THE SINGLE AVATAR for the whole app. Render order:
//   1. `avatarUrl` present  → the photo (Radix Avatar with graceful
//      load/error state-machine — falls back to the default below if the
//      image 404s or is slow, essential for instructor photos from CDNs).
//   2. no/failed photo      → <DefaultAvatar>, the deterministic, storage-
//      free gradient+initials disc keyed on {seed, role?, gender?, name}.
//
// Usage:
//   <Avatar avatarUrl={url} role="student" gender="female"
//           seed={userId} name="نسرین رضوی" size={44} />
//
// The low-level Radix composition primitives (Root/Image/Fallback) are
// still exported as `AvatarRoot` / `AvatarImage` / `AvatarFallback` for the
// rare caller that needs full control (and the Storybook story).
import * as React from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn, tagDisplayName } from "./utils";
import { DefaultAvatar, initialsOf } from "./DefaultAvatar";

export type AvatarSize = "sm" | "md" | "lg" | "xl";

// Named sizes kept for back-compat with the Radix primitive API; the new
// unified <Avatar> takes a numeric pixel size (more flexible for the many
// bespoke sizes across the app).
const SIZE_PX: Record<AvatarSize, number> = { sm: 28, md: 36, lg: 44, xl: 56 };
const SIZE_CLASS: Record<AvatarSize, string> = {
  sm: "w-7 h-7 text-[11px]",
  md: "w-9 h-9 text-[13px]",
  lg: "w-11 h-11 text-[14px]",
  xl: "w-14 h-14 text-[16px]",
};

// ---------------------------------------------------------------------------
// UNIFIED Avatar — photo when present, else the deterministic default.
// ---------------------------------------------------------------------------
export interface AvatarProps {
  /** The served photo path/URL. When absent/empty → the default avatar. */
  avatarUrl?: string | null;
  /** Role id ("student"…) OR colour token ("cyan"…) for the default palette. */
  role?: string;
  /** "male" | "female" | "unspecified" — subtle tint on the default avatar. */
  gender?: string;
  /** Stable identity key (e.g. userId) driving the deterministic colour. */
  seed: string;
  /** Display name → initials + alt text. */
  name?: string | null;
  /** Pixel diameter. Default 40. */
  size?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const Avatar: React.FC<AvatarProps> = ({
  avatarUrl,
  role,
  gender,
  seed,
  name,
  size = 40,
  className,
  style,
}) => {
  const url = (avatarUrl ?? "").trim();
  const label = name ? String(name) : undefined;

  if (url) {
    // Radix gives us the load/error → fallback state machine for free: if
    // the photo fails, the deterministic default shows instead of a broken
    // <img>. The fallback waits 0ms (instant) since we already have it ready.
    return (
      <AvatarPrimitive.Root
        className={cn(
          "relative inline-flex shrink-0 overflow-hidden rounded-full select-none align-middle",
          className,
        )}
        style={{ width: size, height: size, ...style }}
      >
        <AvatarPrimitive.Image
          src={url}
          alt={label ?? ""}
          className="aspect-square h-full w-full object-cover"
        />
        <AvatarPrimitive.Fallback delayMs={0} className="h-full w-full">
          <DefaultAvatar
            seed={seed}
            role={role}
            gender={gender}
            name={name}
            size={size}
          />
        </AvatarPrimitive.Fallback>
      </AvatarPrimitive.Root>
    );
  }

  // No photo → deterministic default directly (no Radix needed).
  return (
    <DefaultAvatar
      seed={seed}
      role={role}
      gender={gender}
      name={name}
      size={size}
      className={className}
      style={style}
    />
  );
};
tagDisplayName(Avatar, "Avatar");

export { initialsOf };
export { SIZE_PX };
export type { AvatarSize as _AvatarSize };

// ---------------------------------------------------------------------------
// Low-level Radix composition primitives (advanced/legacy use).
// ---------------------------------------------------------------------------
export interface AvatarRootProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  size?: AvatarSize;
}

export const AvatarRoot = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarRootProps
>(function AvatarRoot({ className, size = "md", ...props }, ref) {
  return (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        "relative inline-flex shrink-0 overflow-hidden rounded-full select-none",
        "bg-[color:var(--surface-2)]",
        SIZE_CLASS[size],
        className,
      )}
      {...props}
    />
  );
});
tagDisplayName(AvatarRoot, "AvatarRoot");

export const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(function AvatarImage({ className, ...props }, ref) {
  return (
    <AvatarPrimitive.Image
      ref={ref}
      className={cn("aspect-square h-full w-full object-cover", className)}
      {...props}
    />
  );
});
tagDisplayName(AvatarImage, "AvatarImage");

export const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(function AvatarFallback({ className, ...props }, ref) {
  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        "flex h-full w-full items-center justify-center",
        "bg-[color:var(--surface-3)] text-[color:var(--fg-mute)] font-medium",
        className,
      )}
      {...props}
    />
  );
});
tagDisplayName(AvatarFallback, "AvatarFallback");
